import React, { PropsWithChildren, useEffect, useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useBroadcastContext } from '../../hooks/use-broadcast';
import { SocialProviderCitizenContext, CommonCitizenContext } from '../../api-service/clients';
import { InformationSnackbar } from '../../components/notifier';
import { moduleClientFactory } from '../../api-service/module-client-factory';
import { ResourceOperations, ResourceTypes } from '../../authentication/resources';
import { useAllowedActions } from '../../authentication';
import { useServiceProviderUnitSelection } from './hooks/use-service-provider-unit-selection';
import { useFeatureToggles } from '../../feature-toggles/feature-toggles';
import { citizenAreaPathName, authorityAreaPathName } from './authority/citizen-authority-area-pathnames';
import { serviceProviderAreaPathName } from './serviceprovider/citizen-serviceprovider-area-pathnames';
import { CommonCitizenCtx, AuthorityCitizenCtx, ServiceProviderCitizenCtx, AuthorityCitizenContextLegacy } from './citizen-context-hooks';
import { CitizenNotFoundPage } from './citizen-not-found-page';

export type { AuthorityCitizenContextLegacy };

export function WithTestCitizenContext(props: PropsWithChildren<{ context: AuthorityCitizenContextLegacy }>) {
  const { children, context } = props;
  return <AuthorityCitizenCtx.Provider value={context}>{children}</AuthorityCitizenCtx.Provider>;
}

const citizenClient = moduleClientFactory.createCitizenClient(true);
const citizenClientWithoutErrorInterceptor = moduleClientFactory.createCitizenClient(false);

export function WithCitizenContext(props: PropsWithChildren<{ citizenId: string }>) {
  const { citizenId, children } = props;

  const [commonCtx, setCommonCtx] = useState<CommonCitizenContext>({ citizenId } as CommonCitizenContext);
  const [authorityCtx, setAuthorityCtx] = useState<AuthorityCitizenContextLegacy>({ citizenId, isLoading: true } as AuthorityCitizenContextLegacy);
  const [serviceCtx, setServiceCtx] = useState<SocialProviderCitizenContext>({ citizenId } as SocialProviderCitizenContext);
  const [citizenNotFound, setCitizenNotFound] = useState(false);

  const { isFeatureEnabled } = useFeatureToggles();

  const allowedActions = useAllowedActions();

  async function fetchCitizenContext() {
    const hasCitizenAuthorityAccess = allowedActions.isAnyAllowed([{ resourceType: ResourceTypes.Citizen, resourceOperation: ResourceOperations.Read }]);
    const hasCitizenSocialAccess = allowedActions.isAnyAllowed([{ resourceType: ResourceTypes.SocialProviderCitizen, resourceOperation: ResourceOperations.Read }]);
    const hasMedicalCollaboratorCitizenAccess = allowedActions.isAnyAllowed([
      { resourceType: ResourceTypes.MedicalCollaborationCitizen, resourceOperation: ResourceOperations.Read },
    ]);

    if (hasCitizenAuthorityAccess) {
      const authorityCitizenContextResponse = await citizenClient.getAuthorityCitizenContext({ citizenId });

      if (authorityCitizenContextResponse.context?.citizenNotFound) {
        setCitizenNotFound(true);
        return;
      }

      setAuthorityCtx({ ...authorityCitizenContextResponse.context, isLoading: false } as AuthorityCitizenContextLegacy);
      setCommonCtx(authorityCitizenContextResponse.context ?? ({} as CommonCitizenContext));
    }

    if (hasCitizenSocialAccess) {
      const socialCitizenClient = hasCitizenAuthorityAccess ? citizenClientWithoutErrorInterceptor : citizenClient;
      try {
        const socialCitizenContextResponse = await socialCitizenClient.getSocialProviderCitizenContext({ citizenId });

        setServiceCtx(socialCitizenContextResponse.context ?? ({} as SocialProviderCitizenContext));
        setCommonCtx(socialCitizenContextResponse.context ?? ({} as CommonCitizenContext));
      } catch (error) {
        // A citizen outside the user's service provider units is denied here, while the authority context still lets them work
        if (!hasCitizenAuthorityAccess) {
          throw error;
        }
      }
    } else if (hasMedicalCollaboratorCitizenAccess) {
      const medicalCollaboratorContextResponse = await citizenClient.getMedicalCollaboratorCitizenContext({ citizenId });

      setServiceCtx(medicalCollaboratorContextResponse.context ?? ({} as SocialProviderCitizenContext));
      setCommonCtx(medicalCollaboratorContextResponse.context ?? ({} as CommonCitizenContext));
    }
  }

  useEffect(() => {
    if (allowedActions.isLoading) {
      return;
    }
    fetchCitizenContext();
  }, [allowedActions.isLoading]);

  const { currentServiceProvider, setCurrentServiceProvider } = useServiceProviderUnitSelection(serviceCtx.serviceProviderUnits);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  useEffect(() => {
    async function callInformationUpdated() {
      const isMainDetailsPage =
        (window.location.pathname.includes(`/${citizenAreaPathName}/${authorityAreaPathName}/`) ||
          window.location.pathname.includes(`/${citizenAreaPathName}/${serviceProviderAreaPathName}/`)) &&
        window.location.pathname.includes('/details');

      if (isMainDetailsPage && !isFeatureEnabled('Konverteringsproeve_readonly_mode')) {
        // only call on citizen overview
        try {
          const informationUpdatedResult = await citizenClientWithoutErrorInterceptor.syncCitizenInformation({ citizenId });
          if (informationUpdatedResult.informationUpdated) {
            enqueueSnackbar('Informationer på siden er opdateret. De nye oplysninger vil først blive vist når siden genindlæses.', {
              ...InformationSnackbar,
              onClick: () => closeSnackbar(),
            });
          }
        } catch {
          // Hide sync errors since they don't prevent the user from working, even though some citizen master data may not be correct
        }
      }
    }
    callInformationUpdated();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { subscribe: citizenHeaderSubscribe } = useBroadcastContext('ReloadCitizenHeader');
  citizenHeaderSubscribe(() => {
    fetchCitizenContext();
  });

  const serviceProviderContextValue = useMemo(
    () => ({
      ...serviceCtx,
      currentServiceProvider,
      setCurrentServiceProvider,
    }),
    [serviceCtx, currentServiceProvider, setCurrentServiceProvider]
  );

  if (citizenNotFound) {
    return <CitizenNotFoundPage />;
  }

  return (
    <CommonCitizenCtx.Provider value={commonCtx}>
      <AuthorityCitizenCtx.Provider value={authorityCtx}>
        <ServiceProviderCitizenCtx.Provider value={serviceProviderContextValue}>{children}</ServiceProviderCitizenCtx.Provider>
      </AuthorityCitizenCtx.Provider>
    </CommonCitizenCtx.Provider>
  );
}

// Re-export hooks for backward compatibility
export { useCommonCitizenContext, useCitizenContext, useServiceProviderCitizenContext } from './citizen-context-hooks';
export { useIsAnonymousCitizen } from './anonymous-override-context';
